import { RisError, type RisClient } from '../client';
import { type AnalysisJob, type JobDepth, type JobProgress, type JobStart, type ReportSystem, type SimilarCase, type StructuredReport,
  finalMessages, normaliseJob, normaliseJobStart, normaliseProgress, normaliseReport, normaliseSimilar } from './shapes';
import { eventJson, readSse } from './sse';
import {
  type Composed, type FollowupSuggestion, type Followup, type InsertResult, type ModelText, type PatientContext,
  type PatientSummaryDraft, type ApprovedSummary, type PrepareAnswer, type RecistTable, type ReportCheck, type ReportSection, type ToolSwitches,
  normaliseApproved, normaliseComposed, normaliseContext, normaliseFollowup, normaliseInsert, normalisePatientSummary, normalisePrepare,
  normaliseRecist, normaliseReportCheck, normaliseSuggestion, normaliseSummary, normaliseTools,
} from './radiologist/shapes';
import type { FollowupBody, PrepareBody } from './radiologist/logic';

/**
 * The ris AI routes the Asistente uses (viewer-ai contract §3), with the viewer ticket. Every shape
 * from the server is normalised here, so the panel never trusts a field it did not check: old
 * rule-based messages (no findings / actions / model) and new model-backed ones read the same way.
 */

const AI_PREFIX = '/ris/v1/ai';

export const ASSISTANT_MODES = ['chat', 'describe', 'sweep', 'compare', 'quality', 'differentials', 'prior_summary', 'checklist', 'header'] as const;
export type AssistantMode = (typeof ASSISTANT_MODES)[number];

export const FINDING_STATUSES = ['pending', 'accepted', 'corrected', 'rejected'] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];

export type Severity = 'routine' | 'urgent' | 'critical';
export type Box = [x0: number, y0: number, x1: number, y1: number];

/** Where a finding sits: the slice it was seen on and, when the model gave one, its box (0–1, image-relative). */
export interface FindingLocation {
  series_uid: string;
  instance_uid: string | null;
  frame: number;
  index: number | null;
  box: Box | null;
  /** Last stack position of a finding that spans slices (large-study analysis: "cortes 40–46"). */
  index_end?: number | null;
}

export interface AssistantFinding {
  id: string;
  label: string;
  description: string;
  confidence: number | null;
  laterality: string | null;
  severity: Severity;
  status: FindingStatus;
  correction: string | null;
  location: FindingLocation | null;
}

export interface AssistantAction {
  type: string;
  label: string;
  args: Record<string, unknown>;
}

export interface Analysed {
  images: number | null;
  series: string[];
  note: string | null;
}

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string | null;
  mode: string | null;
  isTest: boolean;
  model: string | null;
  modelVersion: string | null;
  findings: AssistantFinding[];
  actions: AssistantAction[];
  analysed: Analysed | null;
}

export interface ViewportContext {
  series_uid: string;
  instance_uid: string;
  frame: number;
  index: number;
  count: number;
  window_width?: number;
  window_center?: number;
}

export interface MeasurementContext {
  label: string;
  value: string;
  unit: string;
}

export interface AskBody {
  message: string;
  mode?: AssistantMode;
  viewport?: ViewportContext;
  prior_study_uid?: string;
  measurements?: MeasurementContext[];
}

export interface AlgorithmRun {
  id: string;
  status: string | null;
  outcome: string | null;
  error: string | null;
  findings: number;
}

// --- normalisers --------------------------------------------------------------------------------

export type Raw = Record<string, unknown>;

export function isRecord(value: unknown): value is Raw {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function str(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

export function num(value: unknown): number | null {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** [x0, y0, x1, y1] in 0–1 with x0 < x1 and y0 < y1, else null. */
export function normaliseBox(value: unknown): Box | null {
  if (!Array.isArray(value) || value.length !== 4) return null;
  const n = value.map(num);
  if (n.some((v) => v === null)) return null;
  const [a, b, c, d] = (n as number[]).map(clamp01);
  const box: Box = [Math.min(a, c), Math.min(b, d), Math.max(a, c), Math.max(b, d)];
  return box[2] > box[0] && box[3] > box[1] ? box : null;
}

function normaliseLocation(value: unknown): FindingLocation | null {
  if (!isRecord(value)) return null;
  const series = str(value.series_uid);
  if (!series) return null;
  return {
    series_uid: series,
    instance_uid: str(value.instance_uid),
    frame: Math.max(1, num(value.frame) ?? 1),
    index: num(value.index),
    box: normaliseBox(value.box),
    index_end: num(value.index_end),
  };
}

function status(value: unknown): FindingStatus {
  return (FINDING_STATUSES as readonly unknown[]).includes(value) ? (value as FindingStatus) : 'pending';
}

function severity(value: unknown): Severity {
  return value === 'urgent' || value === 'critical' ? value : 'routine';
}

export function normaliseFinding(value: unknown): AssistantFinding | null {
  if (!isRecord(value)) return null;
  const id = value.id === undefined || value.id === null ? null : String(value.id);
  const label = str(value.label);
  if (!id || !label) return null;
  return {
    id,
    label,
    description: str(value.description) ?? '',
    confidence: num(value.confidence),
    laterality: str(value.laterality),
    severity: severity(value.severity),
    // Messages carry `status`; the decision routes answer with the finding row, which says `decision`.
    status: status(value.status ?? value.decision),
    correction: str(value.correction),
    location: normaliseLocation(value.location),
  };
}

function normaliseAction(value: unknown): AssistantAction | null {
  if (!isRecord(value)) return null;
  const type = str(value.type);
  if (!type) return null;
  return { type, label: str(value.label) ?? '', args: isRecord(value.args) ? value.args : {} };
}

function normaliseAnalysed(value: unknown): Analysed | null {
  if (!isRecord(value)) return null;
  const series = Array.isArray(value.series) ? value.series.filter((s): s is string => typeof s === 'string') : [];
  return { images: num(value.images), series, note: str(value.note) };
}

export function list<T>(value: unknown, each: (v: unknown) => T | null): T[] {
  return Array.isArray(value) ? value.map(each).filter((v): v is T => v !== null) : [];
}

export function normaliseMessage(value: unknown): AssistantMessage | null {
  if (!isRecord(value)) return null;
  const role = value.role === 'user' || value.role === 'assistant' ? value.role : null;
  if (!role || typeof value.content !== 'string') return null;
  return {
    id: String(value.id ?? ''),
    role,
    content: value.content,
    createdAt: str(value.created_at) ?? str(value.at),
    mode: str(value.mode),
    isTest: value.is_test === true,
    model: str(value.model),
    modelVersion: str(value.model_version),
    findings: list(value.findings, normaliseFinding),
    actions: list(value.actions, normaliseAction),
    analysed: normaliseAnalysed(value.analysed),
  };
}

export function messages(value: unknown): AssistantMessage[] {
  return list(value, normaliseMessage);
}

/** A stream closed before its terminal event (`final` / `done` / `error`). */
export class StreamInterrupted extends Error {
  constructor() {
    super('assistant stream closed before its final event');
  }
}

/** Status a stream `error` event stands for: its own `status` when given, else a bad gateway. */
const STREAM_ERROR_STATUS = 502;

/** An `error` event inside a stream (the HTTP answer itself was 200). */
export class StreamFailed extends RisError {}

function eventStatus(raw: Record<string, unknown>): number | null {
  const status = num(raw.status);
  return status !== null && status >= 400 ? status : null;
}

function streamError(data: unknown): StreamFailed {
  const raw = isRecord(data) ? data : {};
  return new StreamFailed(eventStatus(raw) ?? STREAM_ERROR_STATUS, str(raw.detail) ?? 'stream error');
}

/** What a large-study analysis stream reports, as it happens. */
export interface AnalysisEventHandlers {
  onProgress(progress: JobProgress): void;
  onFinding(finding: AssistantFinding): void;
}

/** How an analysis stream ended. */
export type AnalysisStreamEnd = { kind: 'done'; runId: string | null } | { kind: 'error'; detail: string; status: number | null } | { kind: 'closed' };

// --- routes -------------------------------------------------------------------------------------

export class AssistantApi {
  constructor(private readonly client: RisClient) {}

  async history(studyUid: string): Promise<AssistantMessage[]> {
    return messages(await this.client.call('GET', `${AI_PREFIX}/studies/${studyUid}/assistant`));
  }

  /** The question and its answer, as stored. */
  async ask(studyUid: string, body: AskBody): Promise<AssistantMessage[]> {
    return messages(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/assistant`, body));
  }

  /**
   * The same question on `/assistant/stream`: `onDelta` gets the reply text as the model writes it,
   * the promise the stored messages of the `final` event. An `error` event throws RisError.
   */
  async askStream(studyUid: string, body: AskBody, onDelta: (text: string) => void, signal?: AbortSignal): Promise<AssistantMessage[]> {
    const response = await this.client.stream('POST', `${AI_PREFIX}/studies/${studyUid}/assistant/stream`, body, signal);
    let final: AssistantMessage[] | null = null;
    let failure: RisError | null = null;
    await readSse(response, (event) => {
      const data = eventJson(event);
      if (event.event === 'delta' && isRecord(data) && typeof data.text === 'string') onDelta(data.text);
      else if (event.event === 'final') final = finalMessages(data);
      else if (event.event === 'error') failure = streamError(data);
      return final !== null || failure !== null;
    });
    if (failure) throw failure;
    if (!final) throw new StreamInterrupted();
    // A `final` without a readable answer would leave the question unanswered without a word.
    if (!(final as AssistantMessage[]).some((m) => m.role === 'assistant')) throw new RisError(STREAM_ERROR_STATUS, 'final event without an answer');
    return final;
  }

  /** "Revisar serie" on the whole series: 202 a new job, 200 a done identical one (`cached`). */
  async startAnalysis(studyUid: string, seriesUid: string, depth: JobDepth): Promise<JobStart> {
    const out = normaliseJobStart(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/analyses`, { series_uid: seriesUid, depth }));
    if (!out) throw new RisError(STREAM_ERROR_STATUS, 'analysis without job_id');
    return out;
  }

  async analysis(jobId: string): Promise<AnalysisJob> {
    const out = normaliseJob(await this.client.call('GET', `${AI_PREFIX}/analyses/${encodeURIComponent(jobId)}`));
    if (!out) throw new RisError(STREAM_ERROR_STATUS, 'analysis without id');
    return out;
  }

  /** Follows a job's SSE events (ris pushes them from LISTEN/NOTIFY) until `done` / `error` or the stream closes. */
  async analysisEvents(jobId: string, handlers: AnalysisEventHandlers, signal?: AbortSignal): Promise<AnalysisStreamEnd> {
    const response = await this.client.stream('GET', `${AI_PREFIX}/analyses/${encodeURIComponent(jobId)}/events`, undefined, signal);
    let end: AnalysisStreamEnd = { kind: 'closed' };
    await readSse(response, (event) => {
      const data = eventJson(event);
      if (event.event === 'progress') handlers.onProgress(normaliseProgress(data));
      else if (event.event === 'finding') {
        const finding = normaliseFinding(data);
        if (finding) handlers.onFinding(finding);
      } else if (event.event === 'done') end = { kind: 'done', runId: isRecord(data) ? str(data.run_id) : null };
      else if (event.event === 'error') {
        const raw = isRecord(data) ? data : {};
        end = { kind: 'error', detail: str(raw.detail) ?? '', status: eventStatus(raw) };
      }
      return end.kind !== 'closed';
    });
    return end;
  }

  async cancelAnalysis(jobId: string): Promise<void> {
    await this.client.call('POST', `${AI_PREFIX}/analyses/${encodeURIComponent(jobId)}/cancel`, {});
  }

  /** A structured report (BI-RADS, Lung-RADS…) from the accepted findings and the org's protocols. */
  async structuredReport(studyUid: string, system: ReportSystem): Promise<StructuredReport | null> {
    return normaliseReport(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/structured-report`, { system }));
  }

  /** The org's own cases with similar accepted findings / signed impressions (never this patient's). */
  async similar(studyUid: string, query: string, limit = 10): Promise<SimilarCase[]> {
    const params = new URLSearchParams({ q: query, limit: String(limit) }).toString();
    return normaliseSimilar(await this.client.call('GET', `${AI_PREFIX}/studies/${studyUid}/similar?${params}`));
  }

  /** "Escalar como crítico": a critical finding in the critical-findings module (accepted / corrected only; idempotent). */
  async escalate(findingId: string): Promise<void> {
    await this.client.call('POST', `${AI_PREFIX}/findings/${encodeURIComponent(findingId)}/escalate`, {});
  }

  async transcribe(studyUid: string, audio: Blob, signal?: AbortSignal): Promise<string> {
    const form = new FormData();
    form.append('audio', audio, `dictado.${audio.type.includes('mp4') ? 'm4a' : 'webm'}`);
    form.append('language', 'es');
    const out = await this.client.call<unknown>('POST', `${AI_PREFIX}/studies/${studyUid}/assistant/transcribe`, form, signal);
    return isRecord(out) && typeof out.text === 'string' ? out.text : '';
  }

  /** Aceptar / Corregir / Descartar (409 when someone decided first). */
  async decide(findingId: string, decision: Exclude<FindingStatus, 'pending'>, correction?: string): Promise<AssistantFinding | null> {
    const body = decision === 'corrected' ? { decision, correction } : { decision };
    return normaliseFinding(await this.client.call('POST', `${AI_PREFIX}/findings/${encodeURIComponent(findingId)}/decision`, body));
  }

  async undo(findingId: string): Promise<AssistantFinding | null> {
    return normaliseFinding(await this.client.call('POST', `${AI_PREFIX}/findings/${encodeURIComponent(findingId)}/undo`));
  }

  async runAlgorithm(studyUid: string, key: string, seriesUid: string | null): Promise<AlgorithmRun> {
    const body = seriesUid ? { algorithm: key, series_uid: seriesUid } : { algorithm: key };
    const out = await this.client.call<unknown>('POST', `${AI_PREFIX}/studies/${studyUid}/runs`, body);
    const run = isRecord(out) ? out : {};
    return {
      id: String(run.id ?? ''),
      status: str(run.status),
      outcome: str(run.outcome),
      error: str(run.error),
      findings: Array.isArray(run.findings) ? run.findings.length : 0,
    };
  }
  // --- radiologist tools (contract v3, ris docs/viewer-ai-tools-contract.md) -------------------------

  /** Which of the nine tools the org offers (read once at install). */
  async tools(): Promise<ToolSwitches> {
    return normaliseTools(await this.client.call('GET', `${AI_PREFIX}/tools`));
  }

  /** §2.1: appends `text` to `section` of the caller's draft (opened or resumed by ris), recorded as assisted. */
  async insertIntoReport(studyUid: string, section: ReportSection, text: string): Promise<InsertResult> {
    return normaliseInsert(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/report-draft/insert`, { section, text }));
  }

  /** §2.2: rule checks plus the model's review of the caller's draft (never edits it). */
  async reportCheck(studyUid: string): Promise<ReportCheck> {
    return normaliseReportCheck(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/report-check`, {}));
  }

  /** §2.3: the template's sections written from the dictation; nothing is stored. */
  async reportCompose(studyUid: string, dictation: string, measurements: MeasurementContext[]): Promise<Composed> {
    const body = measurements.length > 0 ? { dictation, measurements } : { dictation };
    return normaliseComposed(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/report-compose`, body));
  }

  /** §2.4: reception, priors and pending follow-ups (data only, no model). */
  async patientContext(studyUid: string): Promise<PatientContext> {
    return normaliseContext(await this.client.call('GET', `${AI_PREFIX}/studies/${studyUid}/context`));
  }

  async contextSummary(studyUid: string): Promise<ModelText> {
    return normaliseSummary(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/context/summary`, {}));
  }

  /** §2.5: a reading plan (viewer actions, real UIDs) for the study's series and priors. */
  async prepare(studyUid: string, body: PrepareBody): Promise<PrepareAnswer> {
    return normalisePrepare(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/prepare`, body));
  }

  /** §2.6: the guideline's follow-up for a reviewed finding (null when ris gave none). */
  async followupSuggestion(findingId: string): Promise<FollowupSuggestion | null> {
    return normaliseSuggestion(await this.client.call('POST', `${AI_PREFIX}/findings/${encodeURIComponent(findingId)}/followup-suggestion`, {}));
  }

  async createFollowup(studyUid: string, body: FollowupBody): Promise<Followup | null> {
    return normaliseFollowup(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/followups`, body));
  }

  /** §2.7: the RECIST 1.1 table of the patient's tracked target lesions. */
  async recist(studyUid: string): Promise<RecistTable> {
    return normaliseRecist(await this.client.call('GET', `${AI_PREFIX}/studies/${studyUid}/recist`));
  }

  /** §2.9: the approved plain-language summary, or null. */
  async patientSummary(studyUid: string): Promise<ApprovedSummary | null> {
    const out = await this.client.call<unknown>('GET', `${AI_PREFIX}/studies/${studyUid}/patient-summary`);
    return normaliseApproved(isRecord(out) ? out.approved : null);
  }

  async generatePatientSummary(studyUid: string): Promise<PatientSummaryDraft> {
    return normalisePatientSummary(await this.client.call('POST', `${AI_PREFIX}/studies/${studyUid}/patient-summary`, {}));
  }

  async approvePatientSummary(studyUid: string, reportId: string, text: string): Promise<ApprovedSummary | null> {
    return normaliseApproved(await this.client.call('PUT', `${AI_PREFIX}/studies/${studyUid}/patient-summary`, { report_id: reportId, text }));
  }
}
